import { PDFArray, PDFCheckBox, PDFDict, PDFDocument, PDFDropdown, PDFHexString, PDFName, PDFOptionList, PDFRadioGroup, PDFRef, PDFSignature, PDFString, PDFTextField, type PDFField } from "pdf-lib";
import { pdfBytesToBlob } from "./utils";
import { needsPicture } from "./drawTextAnywhere";
import { appearancesAreFinal, dropDeadAnnotations, refreshAppearances, setTextFieldWithPicture, type FieldFailure } from "./formAppearance";

const GENERATED_DEFAULT_LIMIT = 100;
const effectiveMaxLength = (n: number | undefined) => (n === GENERATED_DEFAULT_LIMIT ? undefined : n);

export interface FillableFieldEntry {
  name: string;
  /** What to show the user: the field's tooltip when it has one, else a tidied name. */
  label: string;
  field: PDFField;
  kind: "text" | "checkbox" | "dropdown" | "radio" | "list" | "signature" | "unsupported";
  options?: string[];
  /** 0-based page the field is on, when known. */
  page: number | null;
  multiline?: boolean;
  maxLength?: number;
  readOnly?: boolean;
  required?: boolean;
  multiSelect?: boolean;
}

export interface ParsedFillableForm {
  pdfDoc: PDFDocument;
  fields: FillableFieldEntry[];
  initialValues: Record<string, string>;
  initialChecked: Record<string, boolean>;
  initialLists: Record<string, string[]>;
  /** An XFA form (Adobe LiveCycle): can't be filled here. */
  hasXfa: boolean;
}

export interface FillResult { blob: Blob; failed: FieldFailure[]; pictureFields: string[] }

const tidyName = (n: string) => {
  const parts = n.split(".");
  const last = (parts[parts.length - 1] || n).replace(/\[\d+\]$/g, "");
  return last.replace(/[_]+/g, " ").trim() || n;
};

function pageIndexOf(pdfDoc: PDFDocument, field: PDFField): number | null {
  try {
    const widgets = field.acroField.getWidgets();
    if (widgets.length === 0) return null;
    const ref = pdfDoc.context.getObjectRef(widgets[0].dict);
    if (!ref) return null;
    const pages = pdfDoc.getPages();
    for (let i = 0; i < pages.length; i++) {
      const annots = pages[i].node.lookupMaybe(PDFName.of("Annots"), PDFArray);
      if (!annots) continue;
      for (let k = 0; k < annots.size(); k++) {
        const a = annots.get(k);
        if (a instanceof PDFRef && a.objectNumber === ref.objectNumber) return i;
      }
    }
  } catch { /* unknown page */ }
  return null;
}

function tooltipOf(field: PDFField): string | null {
  const tu = field.acroField.dict.get(PDFName.of("TU"));
  if (tu instanceof PDFString || tu instanceof PDFHexString) {
    const t = tu.decodeText().trim();
    return t || null;
  }
  return null;
}

/**
 * Reads a PDF's AcroForm fields into a list a UI can show. Returns the loaded document too:
 * fillPdfForm needs the same live objects to write the answers back.
 */
export async function parsePdfFormFields(file: File): Promise<ParsedFillableForm> {
  const pdfDoc = await PDFDocument.load(new Uint8Array(await file.arrayBuffer()));
  const acro = pdfDoc.catalog.lookupMaybe(PDFName.of("AcroForm"), PDFDict);
  const hasXfa = !!acro?.get(PDFName.of("XFA"));
  const form = pdfDoc.getForm();
  const initialValues: Record<string, string> = {};
  const initialChecked: Record<string, boolean> = {};
  const initialLists: Record<string, string[]> = {};

  const fields: FillableFieldEntry[] = form.getFields().map((field) => {
    const name = field.getName();
    const base = { name, field, label: tooltipOf(field) ?? tidyName(name), page: pageIndexOf(pdfDoc, field), readOnly: field.isReadOnly(), required: field.isRequired() };
    if (field instanceof PDFTextField) {
      initialValues[name] = field.getText() ?? "";
      return { ...base, kind: "text" as const, multiline: field.isMultiline(), maxLength: effectiveMaxLength(field.getMaxLength()) };
    }
    if (field instanceof PDFCheckBox) { initialChecked[name] = field.isChecked(); return { ...base, kind: "checkbox" as const }; }
    if (field instanceof PDFDropdown) {
      const sel = field.getSelected();
      if (sel.length) initialValues[name] = sel[0];
      return { ...base, kind: "dropdown" as const, options: field.getOptions() };
    }
    if (field instanceof PDFRadioGroup) {
      const sel = field.getSelected();
      if (sel) initialValues[name] = sel;
      return { ...base, kind: "radio" as const, options: field.getOptions() };
    }
    if (field instanceof PDFOptionList) {
      initialLists[name] = field.getSelected();
      return { ...base, kind: "list" as const, options: field.getOptions(), multiSelect: field.isMultiselect() };
    }
    if (field instanceof PDFSignature) return { ...base, kind: "signature" as const };
    return { ...base, kind: "unsupported" as const };
  });
  return { pdfDoc, fields, initialValues, initialChecked, initialLists, hasXfa };
}

/**
 * Writes the answers into the form and saves. Answers in any script work. A field that can't be filled is
 * reported in `failed` (never skipped silently), and the other answers are still saved.
 */
export async function fillPdfForm(
  pdfDoc: PDFDocument,
  fields: FillableFieldEntry[],
  values: Record<string, string>,
  checked: Record<string, boolean>,
  flatten: boolean,
  lists: Record<string, string[]> = {},
): Promise<FillResult> {
  const form = pdfDoc.getForm();
  const failed: FieldFailure[] = [];
  const pictureFields: string[] = [];
  const fail = (name: string, reason: string) => failed.push({ name, reason });

  for (const entry of fields) {
    if (entry.readOnly) continue; // "Office use" fields are shown but never written
    try {
      if (entry.kind === "text") {
        const tf = entry.field as PDFTextField;
        const v = values[entry.name] ?? "";
        // A limit of exactly 100 is what many form generators write by default, not a real rule: lift it when needed.
        if (!entry.maxLength && tf.getMaxLength() === GENERATED_DEFAULT_LIMIT && Array.from(v).length > GENERATED_DEFAULT_LIMIT) tf.setMaxLength(undefined);
        if (entry.maxLength && Array.from(v).length > entry.maxLength) { fail(entry.name, `too long (${entry.maxLength} characters at most)`); continue; }
        if (needsPicture(v)) { await setTextFieldWithPicture(pdfDoc, tf, v); pictureFields.push(entry.name); }
        else tf.setText(v);
      } else if (entry.kind === "checkbox") {
        const cb = entry.field as PDFCheckBox;
        if (checked[entry.name]) cb.check(); else cb.uncheck();
      } else if (entry.kind === "dropdown") {
        const v = values[entry.name];
        if (v) (entry.field as PDFDropdown).select(v);
      } else if (entry.kind === "radio") {
        const v = values[entry.name];
        if (v) (entry.field as PDFRadioGroup).select(v);
      } else if (entry.kind === "list") {
        const ol = entry.field as PDFOptionList;
        const v = lists[entry.name] ?? [];
        ol.clear();
        if (v.length) ol.select(v);
      }
    } catch (err) {
      fail(entry.name, String((err as Error)?.message ?? err).slice(0, 120));
    }
  }

  failed.push(...(await refreshAppearances(pdfDoc)));
  appearancesAreFinal(pdfDoc);
  if (flatten) {
    try { form.flatten({ updateFieldAppearances: false }); dropDeadAnnotations(pdfDoc); }
    catch (err) { fail("(flatten)", String((err as Error)?.message ?? err).slice(0, 120)); }
  }
  pdfDoc.setProducer("FreeDailyPro");
  const bytes = await pdfDoc.save({ updateFieldAppearances: false });
  return { blob: pdfBytesToBlob(bytes), failed, pictureFields };
}
